import * as React from "react"
import { useUser } from "@/context/UserContext"
import { ROLES } from "@/lib/roles"
import { fetchCatalog } from "../api/catalogClient"
import { fetchConnections } from "../api/connectionsClient"
import enrichments from "../data/enrichments.json"
import type {
  Connection,
  IntegrationCatalogItem,
  IntegrationEnrichment,
  ItemAvailability,
  RawCatalogItem,
  CategoriesAvailable,
} from "../api/types"

const CDN_URL = (window as any).CDN_URL ?? ""
const IMAGE_FOLDER = "images/integration"
const ENRICHMENTS = enrichments as Record<string, IntegrationEnrichment>

function deriveId(raw: RawCatalogItem): string {
  if (raw.id) return raw.id
  if (raw.img) return raw.img.replace(/\.svg$/i, "").toLowerCase()
  return raw.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")
}

function buildLogoUrl(raw: RawCatalogItem): string {
  if (raw.logo_url) return raw.logo_url
  if (raw.img) {
    return CDN_URL ? `${CDN_URL}/${IMAGE_FOLDER}/${raw.img}` : `/${IMAGE_FOLDER}/${raw.img}`
  }
  return ""
}

function mergeItem(
  raw: RawCatalogItem,
  pricingLocked: boolean,
): IntegrationCatalogItem {
  const id = deriveId(raw)
  const enrich = ENRICHMENTS[id] ?? {}
  const availability: ItemAvailability = enrich.availability ?? "available"
  const isPopular = !!enrich.popular || !!raw.popular
  return {
    id,
    name: raw.name,
    category: raw.category,
    logo: buildLogoUrl(raw),
    auth_mode: raw.auth_mode ?? null,
    availability,
    short_description: enrich.short_description,
    long_description: enrich.long_description,
    capabilities: enrich.capabilities,
    popular: isPopular,
    featured: !!enrich.featured,
    documentation_url: enrich.documentation_url ?? raw.docs ?? undefined,
    docs_connect_url: raw.docs_connect ?? null,
    setup_time_minutes: enrich.setup_time_minutes,
    connection_scope: enrich.connection_scope ?? "org",
    pricing_locked: pricingLocked,
    setup_fields: raw.setup_fields ?? [],
    connect_guide: raw.connect_guide ?? null,
  }
}

function isCategoryLocked(category: string, flags: CategoriesAvailable): boolean {
  if (!(category in flags)) return false
  return flags[category] === false
}

export type CatalogState = {
  isLoading: boolean
  error: Error | null
  items: IntegrationCatalogItem[]
  categories: string[]
  connectionsById: Record<string, Connection>
  /** Pricing flag per category (true = available on plan, false = locked). */
  categoriesAvailable: CategoriesAvailable
}

const INITIAL: CatalogState = {
  isLoading: true,
  error: null,
  items: [],
  categories: [],
  connectionsById: {},
  categoriesAvailable: {},
}

export function useCatalog(): CatalogState {
  const { user } = useUser()
  // Mock connections (sample "Connected/Warning/Error" demo data) are only
  // visible to developers. Everyone else sees the default state — all
  // integrations as "Available" until real Nango connections are wired up.
  const showMockConnections = user?.roleId === ROLES.DEVELOPER
  const [state, setState] = React.useState<CatalogState>(INITIAL)

  React.useEffect(() => {
    let cancelled = false
    const connectionsPromise: Promise<Connection[]> = showMockConnections
      ? fetchConnections()
      : Promise.resolve([])

    Promise.all([fetchCatalog(), connectionsPromise])
      .then(([catalog, connections]) => {
        if (cancelled) return
        const items = catalog.integrations.map((raw) =>
          mergeItem(raw, isCategoryLocked(raw.category, catalog.categoriesAvailable)),
        )
        const categories = Array.from(new Set(items.map((i) => i.category))).sort((a, b) =>
          a.localeCompare(b),
        )
        const connectionsById = Object.fromEntries(
          connections.map((c) => [c.integration_id, c]),
        )
        setState({
          isLoading: false,
          error: null,
          items,
          categories,
          connectionsById,
          categoriesAvailable: catalog.categoriesAvailable,
        })
      })
      .catch((err: Error) => {
        if (cancelled) return
        setState({ ...INITIAL, isLoading: false, error: err })
      })
    return () => {
      cancelled = true
    }
  }, [showMockConnections])

  return state
}
